/**
 * Performance Utilities for Enhanced Race Animation
 *
 * Provides utilities for memory management, cache clearing, and performance monitoring.
 */

import { clearPatternCache } from "./racingPatternGenerator";
import { clearSegmentCache } from "./speedVariationUtils";

/**
 * Clears all caches used by the race animation system
 * Should be called when race is complete or component unmounts
 */
export function clearAllRaceCaches(): void {
  clearPatternCache();
  clearSegmentCache();
}

/**
 * Performance monitoring utilities
 */
export class PerformanceMonitor {
  private static measurements = new Map<string, number[]>();

  /**
   * Start measuring performance for a given operation
   */
  static startMeasurement(_operation: string): number {
    return performance.now();
  }

  /**
   * End measurement and record the result
   */
  static endMeasurement(operation: string, startTime: number): number {
    const endTime = performance.now();
    const duration = endTime - startTime;

    if (!this.measurements.has(operation)) {
      this.measurements.set(operation, []);
    }

    const measurements = this.measurements.get(operation) || [];
    measurements.push(duration);

    // Keep only the last 100 measurements to prevent memory leaks
    if (measurements.length > 100) {
      measurements.shift();
    }

    return duration;
  }

  /**
   * Get average performance for an operation
   */
  static getAveragePerformance(operation: string): number {
    const measurements = this.measurements.get(operation);
    if (!measurements || measurements.length === 0) {
      return 0;
    }

    const sum = measurements.reduce((acc, val) => acc + val, 0);
    return sum / measurements.length;
  }

  /**
   * Clear all performance measurements
   */
  static clearMeasurements(): void {
    this.measurements.clear();
  }

  /**
   * Get performance report for all operations
   */
  static getPerformanceReport(): Record<
    string,
    { average: number; count: number; latest: number }
  > {
    const report: Record<
      string,
      { average: number; count: number; latest: number }
    > = {};

    for (const [operation, measurements] of this.measurements.entries()) {
      if (measurements.length > 0) {
        const sum = measurements.reduce((acc, val) => acc + val, 0);
        report[operation] = {
          average: sum / measurements.length,
          count: measurements.length,
          latest: measurements[measurements.length - 1],
        };
      }
    }

    return report;
  }
}

/**
 * Memory usage utilities
 */
export class MemoryMonitor {
  /**
   * Get current memory usage (if available)
   */
  static getMemoryUsage(): { used: number; total: number } | null {
    if (
      "memory" in performance &&
      (
        performance as {
          memory?: { usedJSHeapSize: number; totalJSHeapSize: number };
        }
      ).memory
    ) {
      const memory = (
        performance as {
          memory: { usedJSHeapSize: number; totalJSHeapSize: number };
        }
      ).memory;
      return {
        used: memory.usedJSHeapSize,
        total: memory.totalJSHeapSize,
      };
    }
    return null;
  }

  /**
   * Check if memory usage is high
   */
  static isMemoryUsageHigh(): boolean {
    const memory = this.getMemoryUsage();
    if (!memory) return false;

    // Consider memory usage high if over 80% of available heap
    return memory.used / memory.total > 0.8;
  }

  /**
   * Force garbage collection (if available and in development)
   */
  static forceGarbageCollection(): void {
    if (
      process.env.NODE_ENV === "development" &&
      "gc" in window &&
      typeof (window as { gc?: () => void }).gc === "function"
    ) {
      (window as { gc: () => void }).gc();
    }
  }
}

/**
 * Debounce utility for performance optimization
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;

  return (...args: Parameters<T>) => {
    if (timeout) {
      clearTimeout(timeout);
    }

    timeout = setTimeout(() => {
      func(...args);
      timeout = null;
    }, wait);
  };
}

/**
 * Throttle utility for performance optimization
 */
export function throttle<T extends (...args: unknown[]) => unknown>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle = false;

  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
      }, limit);
    }
  };
}

/**
 * Request animation frame utility for smooth animations
 */
export class AnimationFrameManager {
  private static activeFrames = new Set<number>();

  /**
   * Request an animation frame and track it
   */
  static requestFrame(callback: FrameRequestCallback): number {
    const frameId = requestAnimationFrame((time) => {
      this.activeFrames.delete(frameId);
      callback(time);
    });

    this.activeFrames.add(frameId);
    return frameId;
  }

  /**
   * Cancel an animation frame
   */
  static cancelFrame(frameId: number): void {
    cancelAnimationFrame(frameId);
    this.activeFrames.delete(frameId);
  }

  /**
   * Cancel all active animation frames
   */
  static cancelAllFrames(): void {
    for (const frameId of this.activeFrames) {
      cancelAnimationFrame(frameId);
    }
    this.activeFrames.clear();
  }

  /**
   * Get count of active animation frames
   */
  static getActiveFrameCount(): number {
    return this.activeFrames.size;
  }
}
